import { useEffect, useState } from "react";
import { Crosshair, Loader2, ShieldCheck, BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtUsd } from "@/lib/api";
import { Hint } from "@/components/Hint";
import { Locked } from "@/components/Locked";

const pct = (v, d = 1) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(d)}%`);
const DEC = { BUY: "bg-emerald-500/15 text-emerald-300 border-emerald-800", WAIT: "bg-amber-500/15 text-amber-300 border-amber-800", AVOID: "bg-red-500/15 text-red-300 border-red-800" };
const CONF = { High: "text-emerald-300", Moderate: "text-cyan-300", Low: "text-amber-300", "Negative edge": "text-red-300", "Insufficient evidence": "text-slate-400" };

export const TradeBracket = (props) => (
  <Locked feature="bracket" title="Rule-based trade bracket" compact desc="Entry, protective stop, profit target, reward:risk and backtested confidence for this coin — plus one-tap stop/target alerts.">
    <TradeBracketInner {...props} />
  </Locked>
);

const TradeBracketInner = ({ symbol, livePrice, coinId, coinName, onArmed }) => {
  const [b, setB] = useState(null); const [err, setErr] = useState(null);
  const [armed, setArmed] = useState(null); const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!symbol) return;
    setB(null); setErr(null);
    api.get(`/backtest/bracket/${symbol}`).then((r) => setB(r.data)).catch((e) => setErr(e?.response?.status === 404 ? "No exchange daily history for this asset — rule-based bracket unavailable." : "Bracket unavailable right now."));
  }, [symbol]);
  useEffect(() => {
    if (!coinId) return;
    api.get(`/alerts/bracket/${coinId}`).then((r) => setArmed(r.data)).catch((e) => console.warn("[apex] bracket alerts:", e?.message || e));
  }, [coinId]);

  if (err) return <div className="apx-card p-4 text-xs text-slate-500" data-testid="bracket-unavailable">{err}</div>;
  if (!b) return <div className="apx-card p-4 text-xs text-slate-500 flex items-center gap-2" data-testid="bracket-loading"><Loader2 className="w-3 h-3 animate-spin" /> Computing trade bracket…</div>;
  const k = b.bracket; const price = livePrice ?? k.entry;
  const lo = k.stop * 0.995, hi = Math.max(k.target, k.trail_arm) * 1.005;
  const y = (v) => `${(1 - (v - lo) / (hi - lo)) * 100}%`;
  const conf = b.confidence;
  const isArmed = !!armed?.armed;
  const fired = (armed?.alerts || []).find((a) => a.triggered);

  const arm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/alerts/bracket", { coin_id: coinId, symbol, name: coinName, entry: k.entry, stop: k.stop, target: k.target, as_of: b.as_of, rules_version: b.rules_version, decision: b.decision, strength: b.strength, risk: b.risk });
      setArmed({ armed: true, bracket_id: r.data.bracket_id, alerts: r.data.alerts });
      toast.success(`Bracket armed — stop ${fmtUsd(k.stop)} · target ${fmtUsd(k.target)}`);
      onArmed?.();
    } catch (e) { toast.error(apiError(e, "Could not arm bracket")); }
    setBusy(false);
  };
  const disarm = async () => {
    setBusy(true);
    try {
      await api.delete(`/alerts/bracket/${coinId}`);
      setArmed({ armed: false, alerts: [] });
      toast("Bracket alerts removed");
      onArmed?.();
    } catch (e) { toast.error(apiError(e, "Could not disarm bracket")); }
    setBusy(false);
  };

  return (
    <div className="apx-card p-4 space-y-3" data-testid="trade-bracket">
      <div className="flex items-center gap-2 flex-wrap">
        <Hint title="Trade bracket" text="Entry, protective stop and profit target computed from EV-IDEX's frozen rules (v1.0) on daily candles — the same rules used in the Strategy Lab backtest." asChild>
          <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400 inline-flex items-center gap-1 cursor-help"><Crosshair className="w-3 h-3 text-cyan-300" /> Rule-based bracket</span>
        </Hint>
        <Hint title={`${b.decision} (frozen rules)`} text={b.decision === "BUY" ? "All nine BUY conditions are met on the latest daily close." : b.decision === "WAIT" ? "Some BUY conditions are unmet; the bracket shows where a trade would sit if they trigger." : "Confirmed downtrend (close below SMA200 with negative MACD) — the rules avoid new longs."} asChild>
          <span className={`text-[10px] font-mono-ticker uppercase tracking-widest px-2 py-0.5 rounded border cursor-help ${DEC[b.decision]}`} data-testid="bracket-decision">{b.decision}</span>
        </Hint>
        <span className="ml-auto text-[10px] font-mono-ticker text-slate-500">as of {b.as_of.slice(0, 10)} · strength {b.strength} · risk {b.risk}</span>
      </div>

      <div className="grid grid-cols-[56px_1fr] gap-3">
        <div className="relative h-44 w-3 mx-auto rounded-full bg-slate-900 border border-slate-800" data-testid="bracket-ladder">
          <div className="absolute left-0 right-0 bg-emerald-500/30" style={{ top: y(k.target), height: `calc(${y(k.entry)} - ${y(k.target)})` }} />
          <div className="absolute left-0 right-0 bg-red-500/30" style={{ top: y(k.entry), height: `calc(${y(k.stop)} - ${y(k.entry)})` }} />
          {[["target", k.target, "#10B981"], ["trail", k.trail_arm, "#06B6D4"], ["entry", k.entry, "#e2e8f0"], ["stop", k.stop, "#EF4444"]].map(([n, v, c]) => (
            <div key={n} className="absolute -left-1.5 w-6 h-0.5" style={{ top: y(v), background: c }} />
          ))}
          <div className="absolute -left-2 w-7 flex justify-center" style={{ top: `calc(${y(Math.min(hi, Math.max(lo, price)))} - 4px)` }}><span className="w-2 h-2 rounded-full bg-amber-300 ring-2 ring-amber-500/40" /></div>
        </div>
        <div className="text-xs font-mono-ticker space-y-1.5">
          <Row c="text-emerald-300" l="Take profit (start exit)" v={fmtUsd(k.target)} s={`${pct(k.target_pct)} · ${k.target_basis}`} tid="bracket-target" />
          <Row c="text-cyan-300" l="Trailing stop arms" v={fmtUsd(k.trail_arm)} s={`then exit if close falls ${(k.trail_pct * 100).toFixed(0)}% from peak`} />
          <Row c="text-slate-100" l="Entry (last daily close)" v={fmtUsd(k.entry)} s={livePrice ? `live now ${fmtUsd(livePrice)}` : null} tid="bracket-entry" />
          <Row c="text-red-300" l="Protective stop" v={fmtUsd(k.stop)} s={`${pct(k.stop_pct)} · ${k.stop_basis}`} tid="bracket-stop" />
          <div className="flex justify-between pt-1 border-t border-slate-800 text-slate-400"><span>Reward : risk</span><span className="text-slate-100" data-testid="bracket-rr">{k.reward_risk ?? "—"} : 1</span></div>
          <div className="flex justify-between text-slate-400"><span>Time stop</span><span>{k.time_stop_days} daily candles</span></div>
        </div>
      </div>

      {coinId && (
        <div className={`rounded-lg border p-3 flex items-center gap-3 ${isArmed ? "border-cyan-900 bg-cyan-950/30" : "border-slate-800 bg-slate-900/40"}`} data-testid="bracket-alerts">
          {isArmed ? <BellRing className="w-4 h-4 text-cyan-300 shrink-0" /> : <BellOff className="w-4 h-4 text-slate-500 shrink-0" />}
          <div className="min-w-0 flex-1 text-[11px]">
            {isArmed ? (
              <><div className="text-cyan-200 font-semibold" data-testid="bracket-armed-label">Bracket alerts armed</div>
              <div className="text-slate-400">You'll be notified the moment price hits the stop ({fmtUsd(armed.alerts.find((a) => a.role === "stop")?.threshold)}) or the target ({fmtUsd(armed.alerts.find((a) => a.role === "target")?.threshold)}). One cancels the other.</div></>
            ) : fired ? (
              <><div className="text-slate-200 font-semibold" data-testid="bracket-fired-label">{fired.role === "stop" ? "Stop" : "Target"} hit @ {fmtUsd(fired.triggered_price)}</div>
              <div className="text-slate-400">Logged to your Decision Journal. Re-arm to watch the current bracket.</div></>
            ) : (
              <><div className="text-slate-200 font-semibold">Get notified at stop or target</div>
              <div className="text-slate-400">Arms two alerts (stop · target) checked live on this page and every 15 min in the background — pushed to your phone if enabled.</div></>
            )}
          </div>
          <Hint title="Bracket alerts" text="Creates a price-below alert at the stop and a price-above alert at the target. When one fires the other is cancelled, and the event is written to your Decision Journal with an indicator snapshot." asChild>
            <button data-testid={isArmed ? "bracket-disarm-btn" : "bracket-arm-btn"} disabled={busy} onClick={isArmed ? disarm : arm}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-lg border font-medium disabled:opacity-60 ${isArmed ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "btn-primary border-transparent"}`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : isArmed ? "Disarm" : "Arm alerts"}
            </button>
          </Hint>
        </div>
      )}

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3" data-testid="bracket-confidence">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`w-4 h-4 ${CONF[conf.tier] || ""}`} />
          <Hint title="Software confidence" text="Computed from the backtested record of comparable setups (same strength bucket, risk and regime, widened until n ≥ 20). It is historical evidence, not a probability that this trade profits." asChild>
            <span className="text-xs font-semibold cursor-help">Confidence: <span className={CONF[conf.tier] || ""} data-testid="confidence-tier">{conf.tier}</span></span>
          </Hint>
          <span className="ml-auto text-[10px] font-mono-ticker text-slate-500">n = {conf.n}</span>
        </div>
        <div className="text-[11px] text-slate-400 mt-1">{conf.reason}. Basis: {b.comparables.basis}{conf.n ? ` — win rate ${(conf.win_rate * 100).toFixed(0)}% (${b.comparables.wins}/${conf.n}), avg hold ${b.comparables.avg_holding_days}d` : ""}.</div>
      </div>

      {b.unmet?.length > 0 && (
        <div className="text-[11px]" data-testid="bracket-unmet">
          <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-amber-300 mb-1">Not yet satisfied ({b.unmet.length})</div>
          <ul className="space-y-0.5 text-slate-400">{b.unmet.map((u) => <li key={u.condition} className="flex gap-2"><span className="text-amber-500/70">○</span><span>{u.condition} <span className="text-slate-600">· {u.value}</span></span></li>)}</ul>
        </div>
      )}
      <div className="text-[10px] text-slate-600 italic">Paper guidance only — no order is ever placed. Levels update on each daily close.</div>
    </div>
  );
};

const Row = ({ c, l, v, s, tid }) => (
  <div className="flex items-baseline justify-between gap-2" data-testid={tid}>
    <span className="text-slate-400">{l}</span>
    <span className="text-right"><span className={`font-semibold ${c}`}>{v}</span>{s && <span className="block text-[10px] text-slate-500">{s}</span>}</span>
  </div>
);
